import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, ChevronRight, Download } from 'lucide-react';
import { redFlagDashboardAPI, type DashRichFilterParams, type RedFlagDashLevel } from '../../services/api';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, SearchBox, StackedBarChart, fmtCount, type BarSegment } from './DashboardPrimitives';
import { BlockLoader, InlineLoader, Spinner } from '../loading';
import { useTableSort, SortTh } from './useTableSort';
import { useStoreRegions } from '../../hooks/useStoreRegions';
import DrillLink from './DrillLink';
import { DRILL_ROUTES, buildDrillUrl, monthRange, yearRange, todayIso } from '../../utils/drilldown';
import { flagTypeHex as flagHex, flagTypeLabel as typeLabel } from '../../utils/redFlagDisplay';
import StatTile from './StatTile';
import { useDashboardList } from './useDashboardList';
import { RED_FLAG_LIST } from './lists/redFlag';

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirror dashboard-redflags.controller responses)

// `label` can be missing or blank for a code an older API does not know (e.g. a
// custom rule's `c_<id>`): every render goes through typeLabel (falls back to the key).
interface TypeMeta { key: string; label: string }
export type RedFlagTypeMeta = TypeMeta;

interface TrendDay {
  date: string;
  label: number;
  byType: Record<string, number>;
  total: number;
}

interface TrendData {
  month: { ym: string; label: string; startDate: string; endDate: string; daysInMonth: number; isCurrentMonth: boolean };
  types: TypeMeta[];
  daily: TrendDay[];
  total: number;
}

interface LeaderRow {
  emp_code: string;
  employee: string;
  mobile: string;
  store: string;
  city: string;
  count: number;
  /** Most recent BEHAVIOUR day the employee was flagged ('YYYY-MM-DD'), or null. */
  last_flag_date?: string | null;
}

interface TodayRow {
  emp_code: string;
  employee: string;
  mobile: string;
  store: string;
  city: string;
  flagTypes: TypeMeta[];
  count: number;
}

interface DrillRow {
  emp_code: string;
  employee: string;
  mobile: string;
  store: string;
  city: string;
  flagTypes: TypeMeta[];
}

// Manager-action metrics (GET /dashboard/action-stats).
/** One store-attribute bucket of the month's flags (server-grouped). */
interface StoreBucket { key: string; flags: number; employees: number }
interface StoreBreakdown {
  total: number;
  byTrade: StoreBucket[];
  byCategory: StoreBucket[];
  byCity: StoreBucket[];
  byState: StoreBucket[];
  unmapped: { flags: number; employees: number };
}
/** Region is rolled up CLIENT-side from the states — there is no region column. */
type BreakdownDim = 'region' | 'trade' | 'category' | 'city';
const BREAKDOWN_DIMS: Array<{ key: BreakdownDim; label: string }> = [
  { key: 'region', label: 'Region' },
  { key: 'trade', label: 'Trade' },
  { key: 'category', label: 'Category' },
  { key: 'city', label: 'City' },
];
const NO_STORE = '— No store mapped —';

interface MgrTatRow {
  managerCode: string | null;
  managerName: string;
  /** Distinct employees with at least one flag in the bucket. Absent from an older API. */
  employeesFlagged?: number;
  flagCount: number;
  actionedCount: number;
  avgTatMs: number | null;
}
interface ActionStats {
  month: { ym: string; label: string; startDate: string; endDate: string; daysInMonth: number; isCurrentMonth: boolean };
  elapsedDays: number;
  generatedCount: number;
  actionedCount: number;
  actionedPerDay: number;
  overallAvgTatMs: number | null;
  byManager: MgrTatRow[];
  /** By Assistant Manager / by Trade Head. Absent from an older API, and empty with
   *  tiers.available false for a project defining neither role. */
  byAm?: MgrTatRow[];
  byTh?: MgrTatRow[];
  tiers?: { available: boolean; partial: boolean };
}

// Per-flag-type colours come from flagTypeHex: fixed for the six standard rules and
// stable BY KEY for a custom rule (a hash of `c_<id>`), so a custom type is the same
// colour in the chart, legend, distribution bar and every row's chips. Any other code
// (e.g. yellow_escalation) keeps its historical colour by position in the list drawn.

// Last-flag day: 'YYYY-MM-DD' → "12 Jun 2026", with "Today"/"Yesterday" for the two most
// recent days so a still-active problem reads at a glance. Parsed as parts (not
// `new Date(str)`) to avoid a UTC-vs-local off-by-one on the date-only string.
const fmtFlagDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return '—';
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return '—';
  const today = new Date();
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(today) - midnight(dt)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Human turnaround time: ms → "2d 3h" / "3h 20m" / "45m" / "<1m" / "—".
const fmtTat = (ms: number | null | undefined): string => {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 1) return '<1m';
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};


// ─────────────────────────────────────────────────────────────────────────────

/**
 * One action-TAT table. Rendered twice — By Assistant Manager and By Trade Head — when
 * the project defines those roles, else once as the original Reporting Manager table.
 * Each instance owns its sort state, so sorting one never reorders the other.
 */
function ActionTatTable({ rows, nameLabel, monthYm }: { rows: MgrTatRow[]; nameLabel: string; monthYm: string }) {
  const sort = useTableSort(rows, {
    manager: (r) => r.managerName,
    employees: (r) => r.employeesFlagged ?? null,
    flags: (r) => r.flagCount,
    actioned: (r) => r.actionedCount,
    tat: (r) => r.avgTatMs,
  });
  const who = nameLabel.toLowerCase();
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
            <SortTh label={nameLabel} sortKey="manager" sort={sort} className="pb-3 font-semibold" />
            <SortTh label="Employees flagged" sortKey="employees" sort={sort} align="right" title={`Employees under this ${who} with at least one red flag this month`} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Red Flags" sortKey="flags" sort={sort} align="right" title={`Red flags raised under this ${who} this month`} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Actioned" sortKey="actioned" sort={sort} align="right" title="Flags marked Done" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
            <SortTh label="Avg TAT" sortKey="tat" sort={sort} align="right" title="Average time from a flag surfacing to it being marked Done" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
          </tr>
        </thead>
        <tbody>
          {sort.sorted.map((r) => (
            <tr key={r.managerCode ?? 'none'} className="border-b border-gray-50 hover:bg-gray-50/50">
              <td className="py-3 font-medium text-gray-900">
                {/* Drill-down: the Red Flags page scoped to this person's whole reporting
                    subtree over the SELECTED MONTH — for a Trade Head, every AM team under
                    them, the same population the row counts. (An AM with other AMs under
                    them, which is rare, opens onto those teams too.) The no-manager bucket
                    has nothing to scope to → plain text. */}
                {r.managerCode ? (
                  <DrillLink
                    href={buildDrillUrl(DRILL_ROUTES.redFlags, {
                      managerCode: r.managerCode,
                      manager: r.managerName,
                      ...monthRange(monthYm),
                    })}
                    title={`Open Red Flags for ${r.managerName}'s team — ${monthYm}`}
                    className="font-medium"
                  >
                    {r.managerName}
                  </DrillLink>
                ) : (
                  r.managerName
                )}
                {r.managerCode && <span className="ml-1 text-xs text-gray-400">({r.managerCode})</span>}
              </td>
              <td className="whitespace-nowrap py-3 pl-4 text-right text-gray-700">{r.employeesFlagged ?? '—'}</td>
              <td className="whitespace-nowrap py-3 pl-4 text-right font-semibold text-gray-900">{r.flagCount}</td>
              <td className="whitespace-nowrap py-3 pl-4 text-right text-gray-700">
                {r.actionedCount}
                {r.flagCount > 0 && (
                  <span className="ml-1 text-xs text-gray-400">({((r.actionedCount / r.flagCount) * 100).toFixed(0)}%)</span>
                )}
              </td>
              <td className="whitespace-nowrap py-3 pl-4 text-right text-gray-700">{fmtTat(r.avgTatMs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function RedFlagTab({
  projectId, month, roleId, filters, flagType, level, onTypesLoaded,
}: {
  projectId: number | null;
  month: string;
  roleId?: string;
  filters?: DashRichFilterParams;
  flagType?: string;
  /** Optional subject-level scope ('frontline' | 'manager'); omitted = everyone. */
  level?: RedFlagDashLevel;
  /**
   * Reports the trend's `types` (the month's rule set, labels from the API) so the
   * page can build its Red Flag Type filter from them. `null` = unavailable (request
   * failed, or an API without `types`). `requestedFlagType` is the type filter the
   * trend was fetched with: a filtered response lists only that type.
   */
  onTypesLoaded?: (types: RedFlagTypeMeta[] | null, requestedFlagType: string | undefined) => void;
}) {
  const monthYm = month;
  // Optional Role filter (comma-separated user_roles.role_id) — empty string when
  // no role is selected. Passed to every red-flag dashboard call.
  const role = roleId && roleId.trim() !== '' ? roleId : undefined;

  const [trend, setTrend] = useState<TrendData | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);

  const [stats, setStats] = useState<ActionStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Month's flags grouped by the flagged employee's store attributes.
  const [breakdown, setBreakdown] = useState<StoreBreakdown | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);
  const [breakdownDim, setBreakdownDim] = useState<BreakdownDim>('region');
  const { regionOf } = useStoreRegions();

  const [leaders, setLeaders] = useState<LeaderRow[]>([]);
  const [leaderYear, setLeaderYear] = useState<string>('');
  const [lbLoading, setLbLoading] = useState(false);
  const [lbError, setLbError] = useState<string | null>(null);
  // Both lists start as an empty array before anything has been asked, so "an answer has
  // arrived" is kept apart: without it the first frame would count nobody.
  const [lbAnswered, setLbAnswered] = useState(false);

  const [today, setToday] = useState<TodayRow[]>([]);
  const [todayDate, setTodayDate] = useState<string>('');
  const [todayLoading, setTodayLoading] = useState(false);
  const [todayError, setTodayError] = useState<string | null>(null);
  const [todayAnswered, setTodayAnswered] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Drill-down (clicked bar segment)
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillRows, setDrillRows] = useState<DrillRow[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearch, setDrillSearch] = useState('');

  // The two full lists open by address (?list=): a card's number and "View All" both
  // write it, and what is open is read from it on every render. Held in state it would
  // be shut by the reload that follows the default Role filter.
  const { list, openList, closeList } = useDashboardList();

  const trendReq = useRef(0);
  // Latest callback in a ref so a new function identity from the parent never
  // re-fires the trend fetch.
  const onTypesLoadedRef = useRef(onTypesLoaded);
  useEffect(() => { onTypesLoadedRef.current = onTypesLoaded; }, [onTypesLoaded]);
  const statsReq = useRef(0);
  const lbReq = useRef(0);
  const todayReq = useRef(0);
  const drillReq = useRef(0);
  const breakdownReq = useRef(0);

  // ── Fetch trend on month / project change ──
  useEffect(() => {
    const reqId = ++trendReq.current;
    setTrendLoading(true);
    setTrendError(null);
    redFlagDashboardAPI
      .getTrend({ month: monthYm, projectId: projectId ?? undefined, roleId: role, flagType, level, ...filters })
      .then((res) => {
        if (reqId !== trendReq.current) return;
        const data = (res.data?.data ?? null) as TrendData | null;
        setTrend(data);
        onTypesLoadedRef.current?.(Array.isArray(data?.types) ? data.types : null, flagType);
      })
      .catch((err) => {
        if (reqId !== trendReq.current) return;
        setTrendError(err.response?.data?.message || 'Failed to load red flag trend');
        setTrend(null);
        onTypesLoadedRef.current?.(null, flagType);
      })
      .finally(() => {
        if (reqId === trendReq.current) setTrendLoading(false);
      });
  }, [monthYm, projectId, role, filters, flagType, level]);

  // ── Fetch the store breakdown — same month / scope contract as the trend ──
  useEffect(() => {
    const reqId = ++breakdownReq.current;
    setBreakdownLoading(true);
    setBreakdownError(null);
    redFlagDashboardAPI
      .getStoreBreakdown({ month: monthYm, projectId: projectId ?? undefined, roleId: role, flagType, level, ...filters })
      .then((res) => {
        if (reqId !== breakdownReq.current) return;
        setBreakdown((res.data?.data ?? null) as StoreBreakdown | null);
      })
      .catch((err) => {
        if (reqId !== breakdownReq.current) return;
        setBreakdownError(err.response?.data?.message || 'Failed to load the store breakdown');
        setBreakdown(null);
      })
      .finally(() => {
        if (reqId === breakdownReq.current) setBreakdownLoading(false);
      });
  }, [monthYm, projectId, role, filters, flagType, level]);

  // Rows for the selected dimension. Trade / Category / City come straight from the
  // server; REGION is rolled up here from the states, because the 1Office master is
  // the only state→region authority (same as every other Region control).
  const breakdownRows = useMemo(() => {
    if (!breakdown) return [] as Array<{ label: string; flags: number; employees: number }>;
    const direct =
      breakdownDim === 'trade' ? breakdown.byTrade
      : breakdownDim === 'category' ? breakdown.byCategory
      : breakdownDim === 'city' ? breakdown.byCity
      : null;
    if (direct) return direct.map((b) => ({ label: b.key || NO_STORE, flags: b.flags, employees: b.employees }));
    const agg = new Map<string, { flags: number; employees: number }>();
    for (const b of breakdown.byState) {
      const label = b.key ? (regionOf(b.key) || 'Region not mapped') : NO_STORE;
      const cur = agg.get(label) ?? { flags: 0, employees: 0 };
      cur.flags += b.flags;
      cur.employees += b.employees;
      agg.set(label, cur);
    }
    return [...agg.entries()]
      .map(([label, v]) => ({ label, ...v }))
      .sort((a, b) => b.flags - a.flags || a.label.localeCompare(b.label));
  }, [breakdown, breakdownDim, regionOf]);

  // ── Fetch action stats (manager TAT + per-day rates) on month / project change ──
  useEffect(() => {
    const reqId = ++statsReq.current;
    setStatsLoading(true);
    redFlagDashboardAPI
      .getActionStats({ month: monthYm, projectId: projectId ?? undefined, roleId: role, flagType, level, ...filters })
      .then((res) => {
        if (reqId !== statsReq.current) return;
        setStats((res.data?.data ?? null) as ActionStats | null);
      })
      .catch(() => { if (reqId === statsReq.current) setStats(null); })
      .finally(() => { if (reqId === statsReq.current) setStatsLoading(false); });
  }, [monthYm, projectId, role, filters, flagType, level]);

  // ── Fetch leaderboard (year) + today on project change ──
  useEffect(() => {
    const reqId = ++lbReq.current;
    setLbLoading(true);
    setLbError(null);
    redFlagDashboardAPI
      .getLeaderboard({ projectId: projectId ?? undefined, roleId: role, flagType, level, ...filters })
      .then((res) => {
        if (reqId !== lbReq.current) return;
        setLeaders((res.data.data?.rows as LeaderRow[]) || []);
        setLeaderYear(res.data.data?.year || '');
        setLbAnswered(true);
      })
      .catch((err) => {
        if (reqId !== lbReq.current) return;
        setLbError(err.response?.data?.message || 'Failed to load leaderboard');
        setLeaders([]);
      })
      .finally(() => {
        if (reqId === lbReq.current) setLbLoading(false);
      });
  }, [projectId, role, filters, flagType, level]);

  useEffect(() => {
    const reqId = ++todayReq.current;
    setTodayLoading(true);
    setTodayError(null);
    redFlagDashboardAPI
      .getToday({ projectId: projectId ?? undefined, roleId: role, flagType, level, ...filters })
      .then((res) => {
        if (reqId !== todayReq.current) return;
        setToday((res.data.data?.rows as TodayRow[]) || []);
        setTodayDate(res.data.data?.today || '');
        setTodayAnswered(true);
      })
      .catch((err) => {
        if (reqId !== todayReq.current) return;
        setTodayError(err.response?.data?.message || 'Failed to load today\'s red flags');
        setToday([]);
      })
      .finally(() => {
        if (reqId === todayReq.current) setTodayLoading(false);
      });
  }, [projectId, role, filters, flagType, level]);

  // The whole-tab "Red Flag Type" filter is the authoritative scope — the trend then
  // returns a single series. Reset the local per-type legend toggle whenever it changes
  // so a stale manual selection can never hide the (now single) filtered series.
  useEffect(() => { setSelected(new Set()); }, [flagType]);

  // Ordered segments (flag types) with colours.
  const segments = useMemo<BarSegment[]>(
    () => (trend?.types || []).map((t, i) => ({ key: t.key, label: typeLabel(t), hex: flagHex(t.key, i) })),
    [trend],
  );
  const visibleKeys = useMemo<Set<string>>(
    () => (selected.size > 0 ? new Set(selected) : new Set(segments.map((s) => s.key))),
    [selected, segments],
  );
  // Month totals per flag type → the distribution card's shares (aggregated from
  // the daily trend, so it always matches the chart above).
  const distribution = useMemo(() => {
    const counts: Record<string, number> = {};
    let total = 0;
    for (const day of trend?.daily ?? []) {
      for (const s of segments) {
        const c = day.byType[s.key] || 0;
        counts[s.key] = (counts[s.key] || 0) + c;
        total += c;
      }
    }
    return { counts, total };
  }, [trend, segments]);
  const toggleSegment = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // ── Drill-down ──
  const openDrill = useCallback(
    (date: string, flagType: string, label: string) => {
      setDrillOpen(true);
      setDrillTitle(`${label} — ${date}`);
      setDrillRows([]);
      setDrillSearch('');
      setDrillLoading(true);
      const reqId = ++drillReq.current;
      redFlagDashboardAPI
        .getTrendDay({ date, flagType, level, projectId: projectId ?? undefined, roleId: role, ...filters })
        .then((res) => {
          if (reqId !== drillReq.current) return;
          setDrillRows((res.data.data?.rows as DrillRow[]) || []);
        })
        .catch(() => {
          if (reqId === drillReq.current) setDrillRows([]);
        })
        .finally(() => {
          if (reqId === drillReq.current) setDrillLoading(false);
        });
    },
    [projectId, role, filters, level],
  );

  const filteredDrillRows = useMemo(() => {
    const q = drillSearch.trim().toLowerCase();
    if (!q) return drillRows;
    return drillRows.filter((r) => [r.employee, r.emp_code, r.store, r.city, r.mobile].some((v) => (v || '').toLowerCase().includes(q)));
  }, [drillRows, drillSearch]);

  const sortedLeaders = useMemo(
    () => [...leaders].sort((a, b) => (b.count - a.count) || a.employee.localeCompare(b.employee)),
    [leaders],
  );

  // Per-column sorting for the two on-page tables (Leaderboard + Today). Fed the
  // exact arrays each table maps, preserving their current default order.
  const leaderSort = useTableSort(sortedLeaders, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    // ISO 'YYYY-MM-DD' sorts correctly as a string; '' (missing) sinks either way.
    lastFlag: (r) => r.last_flag_date || '',
    count: (r) => r.count,
  }, { defaultKey: 'count', defaultDir: 'desc' });
  const todaySort = useTableSort(today, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    // Flags = how many flag chips the row shows (none renders "—" → sinks).
    flags: (r) => r.flagTypes?.length || null,
  });
  const drillSort = useTableSort(filteredDrillRows, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    store: (r) => r.store,
    city: (r) => r.city,
    flags: (r) => r.flagTypes?.length || null,
  });

  // Manager action-TAT tables (each sorts inside ActionTatTable). By Assistant Manager +
  // By Trade Head when the project defines those roles, else the reporting-manager table.
  const mgrRows = stats?.byManager ?? [];
  const amRows = stats?.byAm ?? [];
  const thRows = stats?.byTh ?? [];
  const tiersOn = !!stats?.tiers?.available;
  // Per-day rates share ONE denominator (elapsed days from the server) so the
  // "generated / day" card matches the (live) trend total shown in the chart.
  const elapsedDays = stats?.elapsedDays ?? null;
  const generatedPerDay = trend && elapsedDays ? trend.total / elapsedDays : null;
  const actionedPerDay = stats?.actionedPerDay ?? null;

  // ── CSV download (day-wise red flags, one column per flag type) ──
  const handleDownload = () => {
    if (!trend) return;
    const headers = ['Date', ...trend.types.map(typeLabel), 'Total'];
    const rows = trend.daily.map((d) => [
      d.date,
      ...trend.types.map((t) => fmtCount(d.byType[t.key] || 0)),
      fmtCount(d.total),
    ]);
    downloadCsv(`Red_Flag_Trend_${monthYm}.csv`, buildCsv(headers, rows));
  };

  // ── CSV download (full leaderboard — EVERY flagged employee for the year, not just
  //    the top-10 preview shown on screen), in ranked (red-flag count desc) order. ──
  const handleLeaderboardDownload = () => {
    if (sortedLeaders.length === 0) return;
    const headers = ['Rank', 'Employee', 'Employee Code', 'Store', 'City', 'Mobile Number', 'Last Red Flag', 'Red Flags'];
    const rows = sortedLeaders.map((r, i) => [
      String(i + 1),
      r.employee,
      r.emp_code,
      r.store || '',
      r.city || '',
      r.mobile || '',
      r.last_flag_date || '',
      fmtCount(r.count),
    ]);
    downloadCsv(`Red_Flag_Leaderboard_${leaderYear || 'year'}.csv`, buildCsv(headers, rows));
  };
  const hasTrendData = !!trend && trend.total > 0;

  // ── The two full lists: only from an answer that has arrived ──
  // People on each list, or null while its call is on its way or after it failed. A
  // number that is not known is never drawn as zero, and its list is not shown.
  const flaggedToday = todayAnswered && !todayLoading && !todayError ? today.length : null;
  const leaderCount = lbAnswered && !lbLoading && !lbError ? leaders.length : null;

  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* ─── Module 1: Red Flag Trend ─── */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Red Flag Trend</h2>
            <p className="text-xs text-gray-500">Daily red flags raised, by type — for the selected month</p>
          </div>
          <div className="flex items-center justify-center gap-6 text-center">
            <div className="flex flex-col">
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">Flags this month</span>
              <span className="text-3xl font-bold tabular-nums tracking-tight text-gray-900">{trend ? trend.total : '—'}</span>
            </div>
          </div>
          <div className="flex items-center justify-start gap-2 md:justify-end">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!hasTrendData || trendLoading}
              className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
          </div>
        </div>

        {trend && (
          <div className="mt-3 text-xs text-gray-600">
            <span>Range: {trend.month.startDate} to {trend.month.endDate}</span>
          </div>
        )}
        {/* The Roles filter defaults to store-level roles, and it still applies under the
            Managers level — so without this the manager view can look empty. The page
            passes `level` only while Red Flag Rules is switched on, so this never shows
            while the feature is dark. */}
        {level === 'manager' && role && (
          <p className="mt-2 text-xs text-amber-700">
            Showing managers who hold one of the selected roles. Clear the Roles filter to see every manager.
          </p>
        )}

        {/* Chart */}
        <div className="mt-4">
          {trendLoading ? (
            <BlockLoader className="h-72" />
          ) : trendError ? (
            <div className="flex h-72 items-center justify-center text-sm text-rose-600">{trendError}</div>
          ) : !trend || trend.total === 0 ? (
            <div className="flex h-72 items-center justify-center text-sm text-gray-500">No red flags raised this month.</div>
          ) : (
            <StackedBarChart
              days={trend.daily}
              segments={segments}
              visibleKeys={visibleKeys}
              getCount={(day, key) => day.byType[key] || 0}
              getDayTotal={(day) => day.total}
              onSegmentClick={(day, seg) => openDrill(day.date, seg.key, seg.label)}
              tooltipLines={(_day, _seg, count, dayTotal) => [
                `Count: ${fmtCount(count)}`,
                `Share: ${dayTotal > 0 ? Number(((count / dayTotal) * 100).toFixed(1)) : 0}%`,
              ]}
            />
          )}
        </div>

        {/* Legend (multi-select filter) */}
        {trend && trend.total > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {segments.map((s) => {
              const isSelected = selected.has(s.key);
              const dimmed = selected.size > 0 && !isSelected;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggleSegment(s.key)}
                  className={`inline-flex items-center gap-1.5 text-xs ${dimmed ? 'text-gray-400 opacity-60' : isSelected ? 'font-semibold text-gray-900' : 'text-gray-600'}`}
                >
                  <span className={`h-3 w-3 rounded-sm ${isSelected ? 'ring-2 ring-gray-500 ring-offset-1' : ''}`} style={{ backgroundColor: s.hex }} />
                  {s.label}
                </button>
              );
            })}
            {selected.size > 0 && (
              <button type="button" onClick={() => setSelected(new Set())} className="text-xs font-medium text-primary-600 hover:underline">
                Clear all
              </button>
            )}
          </div>
        )}
      </section>

      {/* ─── Module: Red Flag distribution (share by parameter, mirrors Mood) ─── */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900">Red Flag distribution</h3>
        <p className="text-xs text-gray-500">Share of {distribution.total.toLocaleString()} red flags this month, by parameter</p>
        {distribution.total > 0 ? (
          <>
            <div className="mt-3 flex h-6 overflow-hidden rounded-md">
              {segments.map((s) => {
                const c = distribution.counts[s.key] || 0;
                const w = (c / distribution.total) * 100;
                if (w <= 0) return null;
                return <div key={s.key} title={`${s.label}: ${c}`} style={{ width: `${w}%`, backgroundColor: s.hex }} />;
              })}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-600">
              {segments.map((s) => {
                const c = distribution.counts[s.key] || 0;
                const share = Number(((c / distribution.total) * 100).toFixed(1));
                return (
                  <span key={s.key} className="inline-flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: s.hex }} />
                    {s.label} <span className="font-semibold text-gray-800">{share}%</span>
                  </span>
                );
              })}
            </div>
          </>
        ) : (
          <div className="py-8 text-center text-sm text-gray-500">No red flags to summarise.</div>
        )}
      </section>

      {/* ─── Module: Red flags by store (region / trade / category / city) ─── */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Red flags by store</h3>
            <p className="text-xs text-gray-500">
              Where this month's {(breakdown?.total ?? 0).toLocaleString()} red flags came from — count and share of the month
            </p>
          </div>
          <div className="flex w-fit items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
            {BREAKDOWN_DIMS.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => setBreakdownDim(d.key)}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                  breakdownDim === d.key ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
        {breakdownLoading ? (
          <div className="py-8 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></div>
        ) : breakdownError ? (
          <div className="py-8 text-center text-sm text-rose-600">{breakdownError}</div>
        ) : !breakdown || breakdown.total === 0 || breakdownRows.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">No red flags to summarise.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {breakdownRows.map((r) => {
              const share = breakdown.total > 0 ? (r.flags / breakdown.total) * 100 : 0;
              return (
                <div key={r.label} className="flex items-center gap-3">
                  <span className="w-40 shrink-0 truncate text-sm text-gray-700" title={r.label}>{r.label}</span>
                  <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                    {/* Floor a non-zero share at 1% so a single flag is still visible. */}
                    <span className="block h-full rounded-full bg-primary-500" style={{ width: `${share > 0 ? Math.max(share, 1) : 0}%` }} />
                  </span>
                  <span className="w-24 shrink-0 text-right text-sm">
                    <span className="font-semibold text-gray-900">{r.flags.toLocaleString()}</span>
                    <span className="ml-1 text-xs text-gray-500">({share.toFixed(1)}%)</span>
                  </span>
                  <span className="w-20 shrink-0 text-right text-xs text-gray-400" title="Employees flagged in this group">
                    {r.employees.toLocaleString()} emp
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ─── Module: Red flag action & TAT, by reporting manager ─── */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Red Flag Action &amp; TAT</h3>
          <p className="text-xs text-gray-500">
            {tiersOn
              ? 'How many employees are flagged, and how quickly their red flags are actioned (marked Done), by Assistant Manager and Trade Head'
              : "How quickly reporting managers action (mark Done) their team's red flags"}{' '}
            — for {trend?.month.label || 'the selected month'}
          </p>
        </div>

        {/* KPI strip */}
        {/* The dashboards' own headline tile, so these three read like every other one.
            None opens a list: a rate and a time are not sets of flags. A figure that is
            not known is a dash, never a zero, and its line under it is left out. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatTile
            label="Avg red flags / day"
            value={generatedPerDay == null ? '—' : generatedPerDay.toFixed(1)}
            helper={trend ? `${trend.total.toLocaleString()} over ${elapsedDays ?? '—'} day${elapsedDays === 1 ? '' : 's'}` : undefined}
          />
          <StatTile
            label="Avg actioned / day"
            value={actionedPerDay == null ? '—' : actionedPerDay.toFixed(1)}
            helper={stats ? `${stats.actionedCount.toLocaleString()} marked Done` : undefined}
          />
          <StatTile label="Avg action TAT" value={fmtTat(stats?.overallAvgTatMs)} helper="flag surfaced → marked Done" />
        </div>

        {/* Per-person breakdown */}
        {statsLoading ? (
          <div className="mt-4 flex h-24 items-center justify-center" role="status" aria-label="Loading">
            <Spinner size="md" />
          </div>
        ) : tiersOn ? (
          <>
            {/* Each AM, and the Trade Head above them, side by side — the same flags
                counted at two levels, so each table reconciles with the month on its own. */}
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-gray-900">By Assistant Manager</h4>
                <p className="mb-2 text-xs text-gray-500">Red flags in each Assistant Manager's team</p>
                {amRows.length === 0 ? (
                  <div className="py-6 text-center text-sm text-gray-500">No red flags this month.</div>
                ) : (
                  <ActionTatTable rows={amRows} nameLabel="Assistant Manager" monthYm={monthYm} />
                )}
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-gray-900">By Trade Head</h4>
                <p className="mb-2 text-xs text-gray-500">Every Assistant Manager's team under each Trade Head, combined</p>
                {thRows.length === 0 ? (
                  <div className="py-6 text-center text-sm text-gray-500">No red flags this month.</div>
                ) : (
                  <ActionTatTable rows={thRows} nameLabel="Trade Head" monthYm={monthYm} />
                )}
              </div>
            </div>
            {stats?.tiers?.partial && (
              <p className="mt-2 text-[11px] text-amber-700">
                Some reporting lines couldn't be loaded, so a few teams may be counted under "No Assistant Manager" or
                "No Trade Head". Refresh in a moment.
              </p>
            )}
          </>
        ) : mgrRows.length === 0 ? (
          <div className="mt-4 py-6 text-center text-sm text-gray-500">No red flags for any manager's team this month.</div>
        ) : (
          <div className="mt-4">
            <ActionTatTable rows={mgrRows} nameLabel="Reporting Manager" monthYm={monthYm} />
          </div>
        )}
      </section>

      {/* ─── Modules 2 & 3: Leaderboard + Today ─── */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Leaderboard */}
        <section className="flex min-h-[430px] flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          {/* The header carries the list's headline number too. The title gives way first;
              on a narrow screen the number and buttons drop under it. They wrap in turn
              (never shrink-0): at phone width a four-digit number, Download and View All
              are wider than the card, and the number then sits above the buttons. */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="min-w-0 flex-1 basis-48">
              <h3 className="text-lg font-semibold text-gray-900">Red Flag Leaderboard</h3>
              <p className="text-xs text-gray-500">Total red flags per employee in {leaderYear || 'the year'}</p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
              {leaderCount !== null && (
                <ListCount name="Red Flag Leaderboard" count={leaderCount} onOpen={() => openList(RED_FLAG_LIST.leaderboard)} />
              )}
              {leaders.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleLeaderboardDownload}
                    className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
                  >
                    <Download className="h-3.5 w-3.5" /> Download
                  </button>
                  <button
                    type="button"
                    onClick={() => openList(RED_FLAG_LIST.leaderboard)}
                    className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
                  >
                    View All <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {lbLoading ? (
            <BlockLoader className="flex-1 py-0" />
          ) : lbError ? (
            <div className="flex flex-1 items-center justify-center text-sm text-rose-600">{lbError}</div>
          ) : sortedLeaders.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-sm text-gray-500">No red flags this year.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                  <SortTh label="Employee" sortKey="employee" sort={leaderSort} className="pb-3 font-semibold" />
                  <SortTh label="Mobile Number" sortKey="mobile" sort={leaderSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                  <SortTh label="Last Red Flag" sortKey="lastFlag" sort={leaderSort} title="Most recent day this employee was red-flagged" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                  <SortTh label="Red Flags" sortKey="count" sort={leaderSort} align="right" className="min-w-[110px] whitespace-nowrap pb-3 pl-4 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {leaderSort.sorted.slice(0, 10).map((row) => (
                  <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-3 font-medium text-gray-900">
                      {/* Drill-down: Red Flags for this employee across the LEADERBOARD
                          YEAR — this table is a yearly total and deliberately does not
                          refetch on the dashboard month, so linking the month would show
                          a window that never produced this count. */}
                      <DrillLink
                        href={buildDrillUrl(DRILL_ROUTES.redFlags, {
                          employeeCode: row.emp_code,
                          ...yearRange(leaderYear),
                        })}
                        title={`Open Red Flags for ${row.employee} (${row.emp_code}) — ${leaderYear}`}
                        className="font-medium"
                      >
                        {row.employee}
                      </DrillLink>
                      <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                      <div className="mt-0.5 text-xs font-normal text-gray-500">{[row.store, row.city].filter(Boolean).join(' • ') || '—'}</div>
                    </td>
                    <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                    <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{fmtFlagDate(row.last_flag_date)}</td>
                    <td className="py-3 pl-4 text-right"><span className="font-bold text-rose-500">{row.count}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!lbLoading && !lbError && leaders.length > 10 && (
            <div className="mt-2 text-right text-xs text-gray-400">Top 10 of {leaders.length}</div>
          )}
        </section>

        {/* Today */}
        <section className="flex min-h-[430px] flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="min-w-0 flex-1 basis-48">
              <h3 className="text-lg font-semibold text-gray-900">Red Flags Today</h3>
              <p className="text-xs text-gray-500">Red flags raised today{todayDate ? ` (${todayDate})` : ''}</p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {flaggedToday !== null && (
                <ListCount name="Red Flags Today" count={flaggedToday} onOpen={() => openList(RED_FLAG_LIST.flaggedToday)} />
              )}
              {today.length > 0 && (
                <button
                  type="button"
                  onClick={() => openList(RED_FLAG_LIST.flaggedToday)}
                  className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
                >
                  View All <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          {todayLoading ? (
            <BlockLoader className="flex-1 py-0" />
          ) : todayError ? (
            <div className="flex flex-1 items-center justify-center text-sm text-rose-600">{todayError}</div>
          ) : today.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-sm text-gray-500">No red flags raised today.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                  <SortTh label="Employee" sortKey="employee" sort={todaySort} className="pb-3 font-semibold" />
                  <SortTh label="Mobile Number" sortKey="mobile" sort={todaySort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                  <SortTh label="Flags" sortKey="flags" sort={todaySort} className="pb-3 pl-4 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {todaySort.sorted.slice(0, 10).map((row) => (
                  <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-3 font-medium text-gray-900">
                      {/* Drill-down: Red Flags for this employee on TODAY only — this card
                          is today's raise list and does not follow the dashboard month.
                          `todayDate` is the server's IST date echoed with the data. */}
                      <DrillLink
                        href={buildDrillUrl(DRILL_ROUTES.redFlags, {
                          employeeCode: row.emp_code,
                          startDate: todayDate || todayIso(),
                          endDate: todayDate || todayIso(),
                        })}
                        title={`Open Red Flags for ${row.employee} (${row.emp_code}) — ${todayDate || 'today'}`}
                        className="font-medium"
                      >
                        {row.employee}
                      </DrillLink>
                      <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                      <div className="mt-0.5 text-xs font-normal text-gray-500">{[row.store, row.city].filter(Boolean).join(' • ') || '—'}</div>
                    </td>
                    <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                    <td className="py-3 pl-4"><FlagChips types={row.flagTypes} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!todayLoading && !todayError && today.length > 10 && (
            <div className="mt-2 text-right text-xs text-gray-400">Top 10 of {today.length}</div>
          )}
        </section>
      </div>

      {/* Drill-down modal */}
      {drillOpen &&
        createPortal(
          <Modal onClose={() => setDrillOpen(false)} title={drillTitle}>
            <div className="mb-3">
              <SearchBox value={drillSearch} onChange={setDrillSearch} placeholder="Search by name, code, store…" />
            </div>
            {drillLoading ? (
              <div className="flex h-40 items-center justify-center" role="status" aria-label="Loading"><Spinner size="md" /></div>
            ) : filteredDrillRows.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-500">No employees.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                      <SortTh label="Employee" sortKey="employee" sort={drillSort} className="pb-3 font-semibold" />
                      <SortTh label="Mobile" sortKey="mobile" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                      <SortTh label="Store" sortKey="store" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                      <SortTh label="City" sortKey="city" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                      <SortTh label="Flags" sortKey="flags" sort={drillSort} className="pb-3 pl-4 font-semibold" />
                    </tr>
                  </thead>
                  <tbody>
                    {drillSort.sorted.map((r) => (
                      <tr key={r.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="py-3 font-medium text-gray-900">{r.employee}<span className="ml-1 text-xs text-gray-400">({r.emp_code})</span></td>
                        <td className="py-3 pl-4 text-gray-700">{r.mobile || '—'}</td>
                        <td className="py-3 pl-4 text-gray-700">{r.store || '—'}</td>
                        <td className="py-3 pl-4 text-gray-700">{r.city || '—'}</td>
                        <td className="py-3 pl-4"><FlagChips types={r.flagTypes} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Modal>,
          document.body,
        )}

      {/* The two full lists are open while the address names them and their answer has
          arrived; closing takes the name out of the address. */}
      {/* Leaderboard View-All */}
      {list === RED_FLAG_LIST.leaderboard && leaderCount !== null &&
        createPortal(
          <LeaderboardModal rows={sortedLeaders} year={leaderYear} onClose={closeList} />,
          document.body,
        )}

      {/* Today View-All */}
      {list === RED_FLAG_LIST.flaggedToday && flaggedToday !== null &&
        createPortal(
          <TodayModal rows={today} onClose={closeList} />,
          document.body,
        )}
    </div>
  );
}

// ── A list card's headline number ──
// How many PEOPLE the full list holds, and it says "people": both cards are about red
// flags, and a bare number beside them would be read as a count of flags. Above zero it
// is a button that opens that list (the chevron says so, as on a headline tile); at zero
// it is plain. The card does not draw it at all while the count is not known.
function ListCount({ name, count, onOpen }: { name: string; count: number; onOpen: () => void }): ReactElement {
  const value = count.toLocaleString('en-IN');
  const unit = count === 1 ? 'person' : 'people';
  const body = (
    <>
      <span className="text-3xl font-bold tabular-nums tracking-tight text-gray-900 transition group-hover:text-primary-600">{value}</span>
      <span className="text-xs font-medium text-gray-500">{unit}</span>
    </>
  );
  if (count <= 0) return <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">{body}</span>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${name}: ${value} ${unit}. Open the full list`}
      className="group inline-flex items-baseline gap-1.5 whitespace-nowrap rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      {body}
      <ChevronRight className="h-3.5 w-3.5 self-center text-gray-300 transition group-hover:text-primary-500" aria-hidden />
    </button>
  );
}

// ── Flag-type chips ──
function FlagChips({ types }: { types: TypeMeta[] }) {
  if (!types || types.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t, i) => (
        <span
          key={t.key}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
          style={{ backgroundColor: flagHex(t.key, i) }}
        >
          {typeLabel(t)}
        </span>
      ))}
    </div>
  );
}

// ── Leaderboard View-All modal ──
function LeaderboardModal({ rows, year, onClose }: { rows: LeaderRow[]; year: string; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.employee, r.emp_code, r.store, r.city, r.mobile].some((v) => (v || '').toLowerCase().includes(q)));
  }, [rows, search]);
  const sort = useTableSort(filtered, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    lastFlag: (r) => r.last_flag_date || '',
    count: (r) => r.count,
  });
  // Export the currently visible rows (search + sort applied) so the CSV matches
  // what's on screen. Rank follows the on-screen order.
  const handleDownload = () => {
    if (sort.sorted.length === 0) return;
    const headers = ['Rank', 'Employee', 'Employee Code', 'Store', 'City', 'Mobile Number', 'Last Red Flag', 'Red Flags'];
    const csvRows = sort.sorted.map((r, i) => [
      String(i + 1),
      r.employee,
      r.emp_code,
      r.store || '',
      r.city || '',
      r.mobile || '',
      r.last_flag_date || '',
      fmtCount(r.count),
    ]);
    downloadCsv(`Red_Flag_Leaderboard_${year || 'year'}.csv`, buildCsv(headers, csvRows));
  };
  return (
    <Modal onClose={onClose} title={`Red Flag Leaderboard — ${year}`}>
      <div className="mb-3"><SearchBox value={search} onChange={setSearch} placeholder="Search by name, code, store, city, mobile…" /></div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-xs text-gray-500">{filtered.length} of {rows.length} employees</span>
        <button
          type="button"
          onClick={handleDownload}
          disabled={sort.sorted.length === 0}
          className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Download
        </button>
      </div>
      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">No matching employees.</div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="pb-3 font-semibold">#</th>
                <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 pl-4 font-semibold" />
                <SortTh label="Mobile Number" sortKey="mobile" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label="Last Red Flag" sortKey="lastFlag" sort={sort} title="Most recent day this employee was red-flagged" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label="Red Flags" sortKey="count" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((row, idx) => (
                <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 text-gray-400">{idx + 1}</td>
                  <td className="py-3 pl-4 font-medium text-gray-900">
                    {row.employee}
                    <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                    <div className="mt-0.5 text-xs font-normal text-gray-500">{[row.store, row.city].filter(Boolean).join(' • ') || '—'}</div>
                  </td>
                  <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                  <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{fmtFlagDate(row.last_flag_date)}</td>
                  <td className="py-3 pl-4 text-right"><span className="font-bold text-rose-500">{row.count}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ── Today View-All modal ──
function TodayModal({ rows, onClose }: { rows: TodayRow[]; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.employee, r.emp_code, r.store, r.city, r.mobile].some((v) => (v || '').toLowerCase().includes(q)));
  }, [rows, search]);
  const sort = useTableSort(filtered, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    flags: (r) => r.flagTypes?.length || null,
  });
  return (
    <Modal onClose={onClose} title="Red Flags Today">
      <div className="mb-3"><SearchBox value={search} onChange={setSearch} placeholder="Search by name, code, store, city, mobile…" /></div>
      <div className="mb-2 text-xs text-gray-500">{filtered.length} of {rows.length} employees</div>
      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">No matching employees.</div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
                <SortTh label="Mobile Number" sortKey="mobile" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label="Flags" sortKey="flags" sort={sort} className="pb-3 pl-4 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((row) => (
                <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 font-medium text-gray-900">
                    {row.employee}
                    <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                    <div className="mt-0.5 text-xs font-normal text-gray-500">{[row.store, row.city].filter(Boolean).join(' • ') || '—'}</div>
                  </td>
                  <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                  <td className="py-3 pl-4"><FlagChips types={row.flagTypes} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
